import { useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORMATS = ["Written correspondence", "Phone call", "Video call", "In-person visit"];

const Sticker = ({ state, testid }) => {
  const styles = {
    set: "border-emerald-500/60 text-emerald-400",
    required: "border-amber-500/60 text-amber-400",
    optional: "border-stone-600 text-stone-500",
  };
  const text = { set: "SET", required: "REQUIRED — TO PROGRESS", optional: "OPTIONAL" };
  return (
    <span data-testid={testid}
      className={`ml-3 inline-block rounded-full border px-2.5 py-0.5 text-[10px] tracking-[0.15em] uppercase align-middle ${styles[state]}`}>
      {text[state]}
    </span>
  );
};

export default function AssessmentRequest() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    agent_name: "", agency: "", role_title: "", contact: "",
    reason: "", requested_format: FORMATS[0], family_reference: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [statusRef, setStatusRef] = useState("");
  const [statusResult, setStatusResult] = useState(null);
  const [windowsInfo, setWindowsInfo] = useState(null);
  const [confirmMsg, setConfirmMsg] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const valid = {
    agent_name: form.agent_name.trim().length >= 2,
    agency: form.agency.trim().length >= 2,
    role_title: form.role_title.trim().length >= 2,
    contact: form.contact.trim().length >= 5,
    reason: form.reason.trim().length >= 10,
  };
  const step1Valid = valid.agent_name && valid.agency && valid.role_title && valid.contact;
  const step2Valid = valid.reason;

  const submit = async (e) => {
    e.preventDefault();
    if (!step2Valid) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/encounter/request`, form);
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.detail || "The request could not be sent — try again shortly.");
    } finally {
      setSubmitting(false);
    }
  };

  const checkStatus = async () => {
    setStatusResult(null);
    setWindowsInfo(null);
    setConfirmMsg("");
    setError("");
    try {
      const { data } = await axios.get(`${API}/encounter/status/${statusRef.trim().toUpperCase()}`);
      setStatusResult(data);
      if (["ENCOUNTER_GRANTED", "ENCOUNTER_SCHEDULED"].includes(data.status)) {
        const w = await axios.get(`${API}/encounter/${statusRef.trim().toUpperCase()}/windows`);
        setWindowsInfo(w.data);
      }
    } catch {
      setError("No request found under that reference.");
    }
  };

  const confirmWindow = async (window) => {
    setConfirmMsg("");
    try {
      const { data } = await axios.post(
        `${API}/encounter/${statusRef.trim().toUpperCase()}/confirm`, { window });
      setConfirmMsg(`Encounter set — ${new Date(data.confirmed_window).toLocaleString()}. The family has been notified.`);
      setStatusResult({ ...statusResult, status: data.status });
      setWindowsInfo(null);
    } catch (err) {
      setConfirmMsg(err.response?.data?.detail || "Could not confirm that window.");
    }
  };

  const field =
    "w-full rounded-md border border-stone-700 bg-stone-900/70 px-4 py-3 text-base text-stone-200 placeholder:text-stone-500 focus:outline-none focus:border-[#eec643]";
  const label = "text-xs tracking-[0.2em] uppercase text-stone-400 mb-2";

  return (
    <div data-testid="assessment-page" className="min-h-[100dvh] bg-[#0c0b09] text-stone-200">
      <div className="max-w-2xl mx-auto px-6 py-16 sm:py-24">
        <p className="text-xs tracking-[0.3em] uppercase text-[#eec643] mb-4">
          BGNZN Member Household — Contact Front Door
        </p>
        <h1 className="text-4xl sm:text-5xl font-semibold text-stone-100 leading-tight mb-6">
          Begin in writing.
        </h1>
        <p className="text-base text-stone-400 leading-relaxed mb-4">
          All contact with this member household opens with a written onboarding assessment.
          Every line marked <span className="text-amber-400">REQUIRED</span> must be completed to
          progress — your statement is recorded exactly as written, on an immutable ledger, the
          moment you submit.
        </p>
        <p className="text-base text-stone-400 leading-relaxed mb-12">
          The family reviews every request in writing, and both sides know the purpose of the
          conversation before any encounter is scheduled — nothing proceeds on unknown terms.
        </p>

        {result ? (
          <div data-testid="assessment-confirmation" className="rounded-lg border border-[#eec643]/40 bg-stone-900/60 p-8">
            <p className="text-xs tracking-[0.2em] uppercase text-[#eec643] mb-3">Request received and recorded</p>
            <p className="text-base text-stone-300 mb-6">{result.message}</p>
            <p className="text-sm text-stone-400 mb-1">Your reference — keep it to check status:</p>
            <p data-testid="assessment-reference" className="text-3xl font-mono text-stone-100 tracking-wider">
              {result.reference}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <p data-testid="step-indicator" className="text-xs tracking-[0.25em] uppercase text-stone-500">
              Step {step} of 2 — {step === 1 ? "Identify yourself" : "State the reason"}
            </p>

            {step === 1 && (
              <>
                <div>
                  <label className={label} htmlFor="agent-name">
                    Your full name
                    <Sticker testid="sticker-agent-name" state={valid.agent_name ? "set" : "required"} />
                  </label>
                  <input id="agent-name" data-testid="agent-name-input" className={field}
                    value={form.agent_name} onChange={set("agent_name")} placeholder="e.g. Nancy Mejia" />
                </div>
                <div>
                  <label className={label} htmlFor="agency">
                    Agency / office
                    <Sticker testid="sticker-agency" state={valid.agency ? "set" : "required"} />
                  </label>
                  <input id="agency" data-testid="agency-input" className={field}
                    value={form.agency} onChange={set("agency")} placeholder="The office you represent" />
                </div>
                <div>
                  <label className={label} htmlFor="role">
                    Your role or title
                    <Sticker testid="sticker-role" state={valid.role_title ? "set" : "required"} />
                  </label>
                  <input id="role" data-testid="role-input" className={field}
                    value={form.role_title} onChange={set("role_title")} placeholder="e.g. Initial Assessment Agent" />
                </div>
                <div>
                  <label className={label} htmlFor="contact">
                    How the family can reach you
                    <Sticker testid="sticker-contact" state={valid.contact ? "set" : "required"} />
                  </label>
                  <input id="contact" data-testid="contact-input" className={field}
                    value={form.contact} onChange={set("contact")} placeholder="Phone number or email" />
                </div>
                <button type="button" data-testid="step1-continue-button" disabled={!step1Valid}
                  onClick={() => setStep(2)}
                  className="rounded-full bg-[#eec643] text-stone-950 font-semibold px-8 py-3 text-sm tracking-[0.15em] uppercase transition-opacity hover:opacity-85 disabled:opacity-30 disabled:cursor-not-allowed">
                  Continue
                </button>
                {!step1Valid && (
                  <p data-testid="step1-blocked-note" className="text-xs text-stone-500">
                    Every REQUIRED line above must show SET before you can progress.
                  </p>
                )}
              </>
            )}

            {step === 2 && (
              <>
                <div>
                  <label className={label} htmlFor="format">Requested encounter format</label>
                  <select id="format" data-testid="format-select" className={field}
                    value={form.requested_format} onChange={set("requested_format")}>
                    {FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <label className={label} htmlFor="family-ref">
                    Family reference (if one was given to you)
                    <Sticker testid="sticker-family-ref" state="optional" />
                  </label>
                  <input id="family-ref" data-testid="family-ref-input" className={field}
                    value={form.family_reference} onChange={set("family_reference")} placeholder="Optional" />
                </div>
                <div>
                  <label className={label} htmlFor="reason">
                    Reason for your contact
                    <Sticker testid="sticker-reason" state={valid.reason ? "set" : "required"} />
                  </label>
                  <textarea id="reason" data-testid="reason-input" className={field} rows={5}
                    value={form.reason} onChange={set("reason")}
                    placeholder="State plainly why you are contacting this household — this is shared with the family and recorded exactly as written." />
                </div>
                {error && <p data-testid="assessment-error" className="text-sm text-red-400">{error}</p>}
                <div className="flex items-center gap-4">
                  <button type="button" data-testid="step2-back-button" onClick={() => setStep(1)}
                    className="rounded-full border border-stone-600 px-6 py-3 text-sm tracking-[0.15em] uppercase text-stone-300 transition-colors hover:border-stone-400">
                    Back
                  </button>
                  <button type="submit" data-testid="assessment-submit-button" disabled={submitting || !step2Valid}
                    className="rounded-full bg-[#eec643] text-stone-950 font-semibold px-8 py-3 text-sm tracking-[0.15em] uppercase transition-opacity hover:opacity-85 disabled:opacity-30 disabled:cursor-not-allowed">
                    {submitting ? "Recording…" : "Submit for family review"}
                  </button>
                </div>
                {!step2Valid && (
                  <p data-testid="step2-blocked-note" className="text-xs text-stone-500">
                    The reason line must show SET (ten characters or more) before submission unlocks.
                  </p>
                )}
              </>
            )}
          </form>
        )}

        <div className="mt-20 border-t border-stone-800 pt-10">
          <p className="text-xs tracking-[0.2em] uppercase text-stone-400 mb-4">Already submitted?</p>
          <div className="flex gap-3">
            <input data-testid="status-check-input" className={field} placeholder="Your reference (ENC-…)"
              value={statusRef} onChange={(e) => setStatusRef(e.target.value)} />
            <button onClick={checkStatus} data-testid="status-check-button"
              className="shrink-0 rounded-full border border-stone-600 px-6 py-3 text-sm tracking-[0.15em] uppercase text-stone-300 transition-colors hover:border-[#eec643] hover:text-[#eec643]">
              Check
            </button>
          </div>
          {statusResult && (
            <div className="mt-4">
              <p data-testid="status-check-result" className="text-base text-stone-300">
                {statusResult.reference} — <span className="text-[#eec643]">{statusResult.status.replaceAll("_", " ")}</span>
              </p>
              {windowsInfo && windowsInfo.confirmed_window && (
                <p data-testid="windows-confirmed" className="mt-2 text-sm text-emerald-400">
                  Your encounter is set for {new Date(windowsInfo.confirmed_window).toLocaleString()}.
                </p>
              )}
              {windowsInfo && !windowsInfo.confirmed_window && windowsInfo.proposed_windows.length > 0 && (
                <div className="mt-3" data-testid="windows-offered">
                  <p className="text-sm text-stone-400 mb-2">The family has granted the encounter. Confirm one window:</p>
                  <div className="flex gap-2 flex-wrap">
                    {windowsInfo.proposed_windows.map((w) => (
                      <button key={w} data-testid={`window-confirm-${w}`} onClick={() => confirmWindow(w)}
                        className="rounded-full border border-[#eec643]/60 px-4 py-2 text-sm text-[#eec643] transition-colors hover:bg-[#eec643]/10">
                        {new Date(w).toLocaleString()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {confirmMsg && <p data-testid="window-confirm-msg" className="mt-2 text-sm text-stone-300">{confirmMsg}</p>}
            </div>
          )}
        </div>

        <p className="mt-16 text-xs text-stone-500 leading-relaxed">
          This page records written requests so both parties begin with a clear, shared statement of
          purpose. It does not accept service of process and creates no agreement on either side.
        </p>
      </div>
    </div>
  );
}
